/**
 * Dialog for saving desktop remotely with name validation
 */

import React, { useState, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import PropTypes from "prop-types";
import ModalBase from "../../components/Modals/ModalBase";
import { desktopNameExists } from "../storage/remoteStorage";
import "./DesktopManager.css";

const SaveDesktopDialog = ({ visible, onHide, onSave, existingName = "" }) => {
  const [desktopName, setDesktopName] = useState(existingName || "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (visible) {
      setDesktopName(existingName || "");
      setError("");
    }
  }, [visible, existingName]);

  const validateName = (name) => {
    if (!name || name.trim() === "") {
      return "Nome do desktop é obrigatório";
    }
    if (name.trim().length < 3) {
      return "Nome deve ter pelo menos 3 caracteres";
    }
    if (name.trim().length > 50) {
      return "Nome deve ter no máximo 50 caracteres";
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = validateName(desktopName);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError("");

    try {
      // Check for duplicate name (only if it's a new desktop or name changed)
      const trimmedName = desktopName.trim();
      if (trimmedName.toLowerCase() !== existingName.toLowerCase()) {
        const existingDesktop = await desktopNameExists(trimmedName);
        if (existingDesktop) {
          setError("Já existe um desktop com este nome. Escolha outro nome.");
          setSaving(false);
          return;
        }
      }

      await onSave(trimmedName);
      // Parent component should close the dialog on success
    } catch (err) {
      console.error("Error saving desktop:", err);
      setError(err.message || "Falha ao salvar desktop");
    } finally {
      setSaving(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !saving) {
      handleSave();
    }
  };

  const footer = (
    <div className="modal-footer">
      <Button
        label="Cancelar"
        icon="pi pi-times"
        onClick={onHide}
        className="p-button-text"
        disabled={saving}
        data-evo-id="savedesktopdialog-btn-cancel"
      />
      <Button
        label="Salvar"
        icon="pi pi-check"
        onClick={handleSave}
        disabled={saving}
        loading={saving}
        data-evo-id="savedesktopdialog-btn-save"
      />
    </div>
  );

  return (
    <ModalBase
      header="Salvar Desktop"
      visible={visible}
      onHide={onHide}
      footer={footer}
      size="small"
      variant="form"
      closable={!saving}
      className="save-workspace-dialog"
    >
      <div className="p-fluid">
        <div className="p-field">
          <label htmlFor="desktop-name">Nome do Desktop</label>
          <InputText
            id="desktop-name"
            value={desktopName}
            onChange={(e) => setDesktopName(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Digite o nome do desktop"
            autoFocus
            disabled={saving}
            data-evo-id="savedesktopdialog-input-name"
          />
          {error && (
            <Message
              severity="error"
              text={error}
              className="save-workspace-error"
            />
          )}
          <small
            id="desktop-name-help"
            className="p-text-secondary save-workspace-help"
          >
            Nome será usado para identificar este desktop no servidor
          </small>
        </div>
      </div>
    </ModalBase>
  );
};

SaveDesktopDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  existingName: PropTypes.string,
};

export default SaveDesktopDialog;
